import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import { toast } from 'sonner';
import { createCheckoutSession, createMockCheckoutSession } from '../lib/api';
import { ShippingAddressCard, OrderSummaryCard } from '../components/checkout/CheckoutParts';

const INITIAL_FORM = {
  name: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
};

/** Stripe Checkout refuses to load inside an iframe (security).
 *  Break out of the Emergent preview iframe if we're framed. */
const redirectToCheckout = (url) => {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch (err) {
    // Cross-origin access blocked — fall through to default redirect
    console.warn('Top-frame redirect blocked, using same-frame redirect:', err);
  }
  window.location.href = url;
};

const Checkout = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const set = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total = subtotal;
  const needsShipping = cartItems.some((item) => item.priceType === 'printed');

  const buildPayload = () => ({
    cartItems: cartItems.map((i) => ({
      id: i.id,
      name: i.name,
      priceType: i.priceType,
      price: i.price,
      quantity: i.quantity,
      image: i.image || '',
    })),
    customerInfo: { ...formData },
    origin_url: window.location.origin,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createCheckoutSession(buildPayload());
      if (!res?.url) {
        throw new Error('No checkout URL received');
      }
      // Persist for fallback access on confirmation page
      sessionStorage.setItem('pendingOrderNumber', res.orderNumber);
      redirectToCheckout(res.url);
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSkipPayment = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (!formData.name || !formData.email) {
      toast.error('Please enter your name and email first.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createMockCheckoutSession(buildPayload());
      sessionStorage.setItem('pendingOrderNumber', res.orderNumber);
      navigate(`/confirmation?session_id=${res.session_id}`);
    } catch (err) {
      console.error('Mock checkout failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to create test order.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />

      <section className="pt-40 md:pt-44 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal-ink mb-8">Checkout</h1>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <Card className="border-stone-taupe/20">
                  <CardHeader>
                    <CardTitle className="text-charcoal-ink">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-charcoal-ink">Full Name *</Label>
                      <Input
                        id="name"
                        data-testid="checkout-name-input"
                        required
                        value={formData.name}
                        onChange={(e) => set('name', e.target.value)}
                        className="bg-white border-stone-taupe/20"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-charcoal-ink">Email Address *</Label>
                      <Input
                        id="email"
                        data-testid="checkout-email-input"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => set('email', e.target.value)}
                        className="bg-white border-stone-taupe/20"
                      />
                    </div>
                  </CardContent>
                </Card>

                {needsShipping && <ShippingAddressCard formData={formData} set={set} />}

                <Card className="border-stone-taupe/20">
                  <CardHeader>
                    <CardTitle className="text-charcoal-ink">Secure Payment by Stripe</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-soft-linen p-6 rounded-lg text-center">
                      <p className="text-charcoal-ink mb-2 font-medium">You'll be securely redirected to Stripe to complete your payment.</p>
                      <p className="text-stone-taupe text-sm">All major credit cards accepted.</p>
                    </div>
                  </CardContent>
                </Card>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-submit-button"
                  className="w-full bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory py-6 text-lg"
                >
                  {submitting ? 'Redirecting to Stripe...' : `Pay $${total.toFixed(2)} & Complete Order`}
                </Button>

                {process.env.REACT_APP_ENABLE_MOCK_CHECKOUT === 'true' && (
                  <div className="bg-soft-linen border border-stone-taupe/20 rounded-lg p-4">
                    <p className="text-xs text-stone-taupe mb-2">
                      <strong className="text-charcoal-ink">Preview / Test Mode:</strong> Stripe Checkout cannot load inside the Emergent preview iframe. Use the button below to simulate a completed purchase so you can review the post-purchase flow and Design Details questionnaire.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={submitting}
                      data-testid="checkout-skip-payment-button"
                      onClick={handleSkipPayment}
                      className="w-full border-charcoal-ink text-charcoal-ink hover:bg-warm-ivory"
                    >
                      {submitting ? 'Creating test order...' : 'Skip Payment (Test Mode) →'}
                    </Button>
                  </div>
                )}
              </form>
            </div>

            <div>
              <OrderSummaryCard cartItems={cartItems} subtotal={subtotal} total={total} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
